import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { postData } from '../../utility/Utility';
import { toast } from 'react-toastify';
import CustomLoader from '../../Components/CustomLoader/CustomLoader';

export default function VehicleTypeModal({ 
  show, 
  onClose, 
  onSelect, 
  categoryId,
  subcategoryId,
  selectedId = '' 
}) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // Format status display
  const getStatusBadge = (status) => {
    return status ? 'active' : 'inactive';
  };

  const fetchVehicles = useCallback(async () => {
    if (!categoryId || !subcategoryId) return;
    
    try {
      setLoading(true);
      const response = await postData("api/vehicle/list", {
        category: categoryId,
        subcategory: subcategoryId,
        search: searchTerm,
        page: pagination.page,
        limit: pagination.limit,
        isActive: true,
      });
      
      if (response?.success) {
        setVehicles(response?.data?.vehicles || []);
        setPagination(prev => ({
          ...prev,
          total: response?.data?.totalItems || 0,
          totalPages: response?.data?.totalPages || 1
        }));
      } else {
        throw new Error(response?.message || 'Failed to fetch vehicle types');
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'Error loading vehicle types'
      );
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategoryId, searchTerm, pagination.page, pagination.limit]);

  useEffect(() => {
    if (show && categoryId && subcategoryId) {
      const loadVehicles = async () => {
        await fetchVehicles();
      };
      loadVehicles();
    } else {
      setVehicles([]);
    }
  }, [show, categoryId, subcategoryId, searchTerm, pagination.page, pagination.limit]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVehicles();
  };


  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Vehicle Type</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search vehicle types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
        </div>
        
        {!categoryId || !subcategoryId ? (
          <div className="alert alert-warning">
            Please select both category and subcategory first
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Vehicle Name</th>
                  <th>Description</th>
                  <th>Image</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <CustomLoader loading={true} />
                    </td>
                  </tr>
                ) : vehicles.length > 0 ? (
                  vehicles.map((vehicle, index) => (
                    <tr 
                      key={vehicle._id} 
                      className={selectedId === vehicle._id ? 'table-active' : ''}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {vehicle.vehicleName}
                        {vehicle.subCategory?.title && (
                          <small className="text-muted d-block">Type: {vehicle.subCategory.title}</small>
                        )}
                      </td>
                      <td>{vehicle.description || 'N/A'}</td>
                      <td>
                        {vehicle.image && (
                          <img 
                            src={vehicle.image} 
                            alt={vehicle.vehicleName} 
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/path/to/default-vehicle.png';
                            }}
                            className="img-thumbnail"
                          />
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${vehicle.isActive ? 'success' : 'danger'}`}>
                          {getStatusBadge(vehicle.isActive)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => onSelect(vehicle)}
                          disabled={selectedId === vehicle._id}
                        >
                          {selectedId === vehicle._id ? 'Selected' : 'Select'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      {loading ? 'Loading vehicle types...' : 'No vehicle types found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <nav>
              <ul className="pagination">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setPagination(prev => ({...prev, page: prev.page - 1}))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                  const pageNum = Math.max(1, Math.min(
                    pagination.page - 2,
                    pagination.totalPages - 4
                  )) + i;
                  return (
                    <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setPagination(prev => ({...prev, page: pageNum}))}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setPagination(prev => ({...prev, page: prev.page + 1}))}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
